// MeetingsList.jsx (Assistant Manager Version with Title & Date Filter + Calendar Highlight)
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import Swal from "sweetalert2";
import { BsInfoCircle } from "react-icons/bs";
import { format, isSameDay, isAfter } from "date-fns";
import { useNavigate } from "react-router-dom";

const MeetingsList = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [titleFilter, setTitleFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/get-meetings`,
        { withCredentials: true }
      );
      if (res.data.status) {
        setMeetings(res.data.meetings);
      }
    } catch (err) {
      console.error("Failed to fetch meetings", err);
    }
  };

  const futureMeetings = meetings.filter((m) => isAfter(new Date(m.date), new Date()));
  const meetingDates = futureMeetings.map((m) => new Date(m.date));

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      return meetingDates.some((d) => isSameDay(d, date)) ? "highlight" : null;
    }
    return null;
  };

  // Filter meetings by title and date
  const filteredMeetings = futureMeetings.filter((m) => {
    const matchesTitle = m.title.toLowerCase().includes(titleFilter.toLowerCase());
    const matchesDate = selectedDate ? isSameDay(new Date(m.date), selectedDate) : true;
    return matchesTitle && matchesDate;
  });

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="row mb-3">
        <h3>Manage Meetings</h3>
      </div>

      <div className="row g-4">
        <div className="col-lg-12">
          <div className="card shadow-sm">
            <div className="card-body d-flex flex-wrap gap-4 justify-content-center">

              {/* Calendar */}
              <Calendar
                tileClassName={tileClassName}
                showDoubleView={true}
                defaultValue={new Date()}
                onClickDay={(date) => setSelectedDate(date)}
              />

              <div className="w-100 mt-4">
                {/* Filters */}
                <div className="d-flex mb-3 align-items-center gap-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by title..."
                    value={titleFilter}
                    onChange={(e) => setTitleFilter(e.target.value)}
                  />
                  {selectedDate && (
                    <div>
                      <span className="badge bg-primary p-2 me-2">
                        Date: {format(selectedDate, "yyyy-MM-dd")}
                      </span>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setSelectedDate(null)}
                      >
                        Clear Date
                      </button>
                    </div>
                  )}
                </div>

                {/* Table */}
                <table className="table table-bordered table-hover shadow-sm nowrap">
                  <thead className="table-success">
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Date & Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMeetings.map((meeting, idx) => (
                      <tr key={meeting.id}>
                        <td>{idx + 1}</td>
                        <td>{meeting.title}</td>
                        <td>
                          {meeting.meeting_datetime
                            ? new Date(meeting.meeting_datetime + "Z").toLocaleString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "-"}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-info me-2"
                            onClick={() => navigate(`/assistant-manager/meetings/view/${meeting.id}`)}
                          >
                            <BsInfoCircle /> View
                          </button>
                        </td>
                      </tr>
                    ))}

                    {filteredMeetings.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No meetings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingsList;